import { useState, useEffect } from "react";
import { Paper, Box, Typography, Button, Grid, TextField, IconButton, Stack, Divider } from "@mui/material";
import { Add, Delete, Edit, Save, Download } from "@mui/icons-material";
import { usePDF } from "react-to-pdf";

import MDBox from "../../components/MDBox";
import MDTypography from "../../components/MDTypography";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DefaultNavbar from "../../components/TopNavBar/DefaultNavBar";

function RFPGenerator() {
    /*
    const [rfpSections, setRfpSections] = useState([
        {
            id: "project-overview",
            title: "Project Overview",
            content: "",
            type: "text"
        },
        {
            id: "requirements",
            title: "Requirements",
            items: [],
            type: "list"
        },
        {
            id: "specifications",
            title: "Technical Specifications",
            items: [],
            type: "list"
        },
        {
            id: "evaluation-criteria",
            title: "Evaluation Criteria",
            items: [],
            type: "list"
        },
        {
            id: "timeline",
            title: "Project Timeline",
            content: "",
            type: "text"
        },
        {
            id: "budget",
            title: "Budget Considerations",
            content: "",
            type: "text"
        }
    ]);

    const [editingId, setEditingId] = useState(null);
    const [editText, setEditText] = useState("");
    const [newItem, setNewItem] = useState("");
    const [isAddingItem, setIsAddingItem] = useState(false);
    const [activeSection, setActiveSection] = useState(null);

    // Initialize RFP with project data when available
    useEffect(() => {
        if (projectData?.projects) {
            const currentProject = projectData.projects.find(p => p.title === projectName);
            if (currentProject) {
                setRfpSections(prev => prev.map(section => {
                    if (section.id === "project-overview") {
                        return {
                            ...section,
                            content: currentProject.description || ""
                        };
                    }
                    if (section.id === "requirements" && currentProject.requirements) {
                        return {
                            ...section,
                            items: currentProject.requirements.map(req => ({
                                id: Date.now() + Math.random(),
                                text: `${req.title}: ${req.description}`
                            }))
                        };
                    }
                    return section;
                }));
            }
        }
    }, [projectData, projectName]);

    // Add asset specifications when available
    useEffect(() => {
        if (assetData?.assets) {
            setRfpSections(prev => prev.map(section => {
                if (section.id === "specifications") {
                    const assetSpecs = assetData.assets.flatMap(asset => 
                        asset.specifications?.map(spec => ({
                            id: Date.now() + Math.random(),
                            text: `${spec.name}: ${spec.description}`
                        })) || []
                    );
                    return {
                        ...section,
                        items: [...section.items, ...assetSpecs]
                    };
                }
                return section;
            }));
        }
    }, [assetData]);

    const { toPDF, targetRef } = usePDF({
        filename: `${projectName || 'rfp'}.pdf`,
        page: {
            format: 'a4',
            orientation: 'portrait',
        },
        overrides: {
            pdf: {
                compress: true,
                margin: 20,
            },
        },
    });

    const handleEditStart = (section) => {
        setEditingId(section.id);
        setEditText(section.content || "");
    };

    const handleEditSave = () => {
        setRfpSections(prev => prev.map(section => 
            section.id === editingId 
                ? { ...section, content: editText }
                : section
        ));
        setEditingId(null);
    };

    const handleEditCancel = () => {
        setEditingId(null);
    };

    const handleAddItem = (sectionId) => {
        if (!newItem.trim()) return;

        setRfpSections(prev => prev.map(section => 
            section.id === sectionId
                ? {
                    ...section,
                    items: [...section.items, { id: Date.now(), text: newItem.trim() }]
                }
                : section
        ));
        setNewItem("");
        setIsAddingItem(false);
    };

    const handleDeleteItem = (sectionId, itemId) => {
        setRfpSections(prev => prev.map(section => 
            section.id === sectionId
                ? {
                    ...section,
                    items: section.items.filter(item => item.id !== itemId)
                }
                : section
        ));
    };

    const renderSection = (section) => {
        if (section.type === "text") {
            if (editingId === section.id) {
                return (
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <TextField
                            fullWidth
                            multiline
                            rows={4}
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            sx={{ mb: 2 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                                size="small"
                                onClick={handleEditCancel}
                                sx={{ color: 'text.secondary' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={handleEditSave}
                                sx={{
                                    bgcolor: "#2B5587",
                                    color: "#FFFFFF !important",
                                    '&:hover': {
                                        bgcolor: '#3a6ca8',
                                    },
                                }}
                            >
                                Save
                            </Button>
                        </Box>
                    </Paper>
                );
            }

            return (
                <Paper sx={{ p: 2, mb: 2 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="h6">{section.title}</Typography>
                        <IconButton
                            size="small"
                            onClick={() => handleEditStart(section)}
                            sx={{ color: 'text.secondary' }}
                        >
                            <Edit />
                        </IconButton>
                    </Box>
                    <Typography>{section.content}</Typography>
                </Paper>
            );
        }

        return (
            <Paper sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6">{section.title}</Typography>
                    <IconButton
                        size="small"
                        onClick={() => {
                            setIsAddingItem(true);
                            setActiveSection(section.id);
                        }}
                        sx={{ color: 'text.secondary' }}
                    >
                        <Add />
                    </IconButton>
                </Box>
                <Stack spacing={2}>
                    {section.items.map((item) => (
                        <Box key={item.id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="body2" sx={{ flexGrow: 1 }}>
                                • {item.text}
                            </Typography>
                            <IconButton
                                size="small"
                                onClick={() => handleDeleteItem(section.id, item.id)}
                                sx={{ color: 'text.secondary' }}
                            >
                                <Delete />
                            </IconButton>
                        </Box>
                    ))}
                </Stack>
            </Paper>
        );
    };

    const handleDownloadPDF = async () => {
        try {
            await toPDF();
        } catch (error) {
            console.error('Error generating PDF:', error);
        }
    };

    return (
        <DashboardLayout>
            <DefaultNavbar />
            <MDBox bgcolor="#dee2e6">
                <Paper sx={{ p: 2, mb: 2 }}>
                    <MDTypography variant="h5" gutterBottom>
                        RFP Generator - {projectName || "New Project"}
                    </MDTypography>
                    <Button
                        variant="contained"
                        startIcon={<Download />}
                        onClick={handleDownloadPDF}
                        sx={{
                            bgcolor: "#2B5587",
                            color: "#FFFFFF !important",
                            '&:hover': {
                                bgcolor: '#3a6ca8',
                            },
                        }}
                    >
                        Download RFP
                    </Button>
                </Paper>

                {isAddingItem && (
                    <Paper sx={{ p: 2, mb: 2 }}>
                        <MDTypography variant="h6" gutterBottom>
                            Add New Item
                        </MDTypography>
                        <TextField
                            fullWidth
                            value={newItem}
                            onChange={(e) => setNewItem(e.target.value)}
                            placeholder="Enter item text"
                            sx={{ mb: 2 }}
                        />
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                            <Button
                                size="small"
                                onClick={() => {
                                    setIsAddingItem(false);
                                    setActiveSection(null);
                                }}
                                sx={{ color: 'text.secondary' }}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleAddItem(activeSection)}
                                disabled={!newItem.trim()}
                                sx={{
                                    bgcolor: "#2B5587",
                                    color: "#FFFFFF !important",
                                    '&:hover': {
                                        bgcolor: '#3a6ca8',
                                    },
                                }}
                            >
                                Add Item
                            </Button>
                        </Box>
                    </Paper>
                )}

                <Box ref={targetRef}>
                    {rfpSections.map((section) => (
                        <Box key={section.id}>
                            {renderSection(section)}
                        </Box>
                    ))}
                </Box>
            </MDBox>
        </DashboardLayout>
    ); */
}

export default RFPGenerator;